// useAuthentication.ts
import { useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { LoginStackParamList } from '../../navigators/LoginNavigator';
import firebaseService from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { useCustomAlert } from '../useCustomAlert';
import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';
import { API_URL } from '@env';
import { User } from './UserTypes';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import TriBoolean from '../utilities/ThreeBoolean';
import { useDebugLogger } from '../utilities/useDebugLogger';

// Import Firebase web SDK at the module level - these will only be used on web
import * as firebaseWeb from 'firebase/auth';

// For React Native Firebase, direct object access is needed
const nativeSignIn = async (email: string, password: string) => {
  if (!firebaseService.auth) {
    throw new Error("Firebase auth is not initialized");
  }
  
  // Use auth directly as an object
  const auth = firebaseService.auth as any;
  
  // Use auth directly (it's already an instance, not a function)
  const userCredential = await auth.signInWithEmailAndPassword(email, password);
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error("Authentication succeeded but user is null");
  }
  
  const idToken = await currentUser.getIdToken();
  const auth0Id = currentUser.uid;
  
  return { idToken, auth0Id };
};

const nativeSignOut = async () => {
  if (!firebaseService.auth) {
    throw new Error("Firebase auth is not initialized");
  }
  
  // Use auth directly without calling it as a function
  const auth = firebaseService.auth as any;
  await auth.signOut();
};

const nativeResetPassword = async (email: string) => {
  if (!firebaseService.auth) {
    throw new Error("Firebase auth is not initialized");
  }
  
  // Use auth directly without calling it as a function
  const auth = firebaseService.auth as any;
  await auth.sendPasswordResetEmail(email);
};

/**
 * Custom hook for handling user authentication.
 */
export const useAuthentication = () => {

  const [loading, setLoading] = useState(false);
  const { isAuthenticated, setIsAuthenticated, setUserProfile, userProfile } = useAuth();
  const navigation = useNavigation<StackNavigationProp<LoginStackParamList>>();
  const { showCustomAlert, hideCustomAlert } = useCustomAlert();

  // States for managing CustomAlert
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  const { unauthenticatedApiCall } = useAuthenticatedApi();

  const debug = useDebugLogger();

  /**
   * Fetches and updates the top users data.
   */
  const fetchAndUpdateTopUsers = async () => {
    try {

      const responseData = await unauthenticatedApiCall(
        'users/topMonthlyLikes/getTopUsers',
        'GET'
      );

      const topUsers = responseData;

      // Store the top users data
      const topUsersStorageObject = JSON.stringify({
        topUsers: topUsers.map((user: any) => ({ 
          username: user.username, 
          bio: user.bio, 
          profilePicture: user.profilePicture 
        })),
        lastFetched: new Date().toISOString(),
      });

      await AsyncStorage.setItem('topUsersData', topUsersStorageObject);
    } catch (error: any) {
      debug.error('Error fetching top users:', error);
    }
  };

  /**
   * Handles the login process with platform-specific implementation.
   */
  const handleLogin = async (email: string, password: string) => {
    setLoading(true);
    try {
      let idToken: string;
      let auth0Id: string;

      if (Platform.OS === 'web') {
    
        // Use Firebase directly from the import
        const auth = firebaseWeb.getAuth();
        await firebaseWeb.setPersistence(auth, firebaseWeb.inMemoryPersistence);
        const userCredential = await firebaseWeb.signInWithEmailAndPassword(auth, email, password);
        
        idToken = await userCredential.user.getIdToken();
        auth0Id = userCredential.user.uid;
      } else {
        const result = await nativeSignIn(email, password);
        idToken = result.idToken;
        auth0Id = result.auth0Id;
      }

      // Send the idToken to server
      
      const userInfoResponse = await axios.post(
        `${API_URL}/users/auth/login`,
        { idToken, auth0Id },
        { withCredentials: true }
      );

      if (userInfoResponse.data) {
        const userProfile = userInfoResponse.data.user;

        const userData: User = {
          username: userProfile.username,
          bio: userProfile.bio,
          watchedVideos: userProfile.watchedVideos,
          profilePicture: userProfile.profilePicture,
          blockedUserIds: userProfile.blockedUserIds,    
          correctSoundUrl: userProfile.correctSound,
          incorrectSoundUrl: userProfile.incorrectSound,
          followersCount: userProfile.followersCount,
          followingCount: userProfile.followingCount,
          followersUserIds: userProfile.followersUserIds,
          followingUserIds: userProfile.followingUserIds,
          uploadedVideos: userProfile.uploadedVideos,
        };

        await AsyncStorage.setItem('userProfile', JSON.stringify(userData));
        setUserProfile(userData as User);
        fetchAndUpdateTopUsers();
        setIsAuthenticated(TriBoolean.TRUE);
      }

      return true;
    } catch (error: any) {
      const errorMessage = error.code === 'auth/wrong-password' 
        ? 'The password is incorrect. Please try again.' 
        : error.message;
      showCustomAlert('Login Failed', errorMessage);
      debug.error('Error during login:', error);
      setAlertTitle('Login Failed');
      setAlertMessage(error.message || 'An unexpected error occurred');
      setAlertVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (Platform.OS === 'web') {
        // Web-specific logout
        const auth = firebaseWeb.getAuth();
        await firebaseWeb.signOut(auth);
      } else {
        // React Native specific logout using our helper function
        await nativeSignOut();
      }
  
      // Tell server to clear the session cookie
      await unauthenticatedApiCall(
        `users/auth/logout`,
        'POST'
      );
  
      // Cleanup local state
      setIsAuthenticated(TriBoolean.FALSE);
      setUserProfile({} as User);
      AsyncStorage.removeItem('userProfile');
      navigation.navigate('LoginScreen');
    } catch (error) {
      debug.error("Logout error:", error);
    }
  };

  const isValidEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleForgotPassword = async (email: string) => {
    if (!email || !isValidEmail(email)) {
      setAlertTitle('Invalid Email');
      setAlertMessage('Please enter a valid email address to reset your password.');
      setAlertVisible(true);
      return;
    }

    try {
      if (Platform.OS === 'web') {
        // Web-specific password reset
        const auth = firebaseWeb.getAuth();
        await firebaseWeb.sendPasswordResetEmail(auth, email);
      } else {
        // React Native specific password reset using our helper function
        await nativeResetPassword(email);
      }

      setAlertTitle('Check Your Email');
      setAlertMessage('If your account is valid, a link to reset your password will be sent.');
      setAlertVisible(true);
    } catch (error: any) {
      setAlertTitle('Error');
      setAlertMessage(error.message || 'Failed to send password reset email');
      setAlertVisible(true);
      debug.error('Error sending password reset email:', error);
    }
  };

  return {
    loading,
    handleLogin,
    handleLogout,
    showCustomAlert,
    hideCustomAlert,
    alertVisible,
    setAlertVisible,
    alertMessage,
    alertTitle,
    isValidEmail, 
    handleForgotPassword
  };
};